import React from 'react';
import { Component } from '../../dispatcher/modules';
import PuzzleTabStrip from '../../components/UI/tab_strip/PuzzleTabStrip';
import {
  TabStrip,
  TabStripTab,
  TabStripSelectEventArguments
} from '@progress/kendo-react-layout';
import { Position } from '../../dispatcher/Types';
import {
  ADD_MULTIPLE_TABS,
  TAB_PANEL_CONFIG,
  TAB_PANEL_FORM_PROPS,
  TAB_PANEL_FORM_STATE
} from './Types';
import * as _ from 'lodash';
import HBox from '../layout/container/h_box/HBox';
import uuid from 'uuid-random';

export default class TabPanelForm extends Component<TAB_PANEL_FORM_PROPS, TAB_PANEL_FORM_STATE> {
  private refChildren = []
  private additionalGetData = []
  private additionalSetData = []
  private tabPanelStripRef: any = React.createRef();

  constructor(props:TAB_PANEL_FORM_PROPS) {
    super(props);
    let isValid = new Array(this.props.tabPanelConfig.length).fill(false);
    const tabPanelConfig = this.props.tabPanelConfig.map((tab:any) => {
      this.additionalGetData = [...this.additionalGetData, !!tab.additionalGetData];
      this.additionalSetData = [...this.additionalSetData, !!tab.additionalSetData];
      return ({ ...tab, key: uuid() });
    });
    const tabCount = tabPanelConfig.length;

    this.setUpReferences(tabCount);

    this.state = {
      selected: 0,
      tabCount: tabCount,
      tabPanelConfig: tabPanelConfig,
      isValid: isValid,
      isAllValid: false,
      data: []
    };
  }

  state:TAB_PANEL_FORM_STATE = {
    selected: 0,
    tabCount: 0,
    tabPanelConfig: [],
    isValid: [],
    isAllValid: false,
    data: []
  };

  setUpReferences(tabCount: number) {
    this.refChildren = Array.from({ length: tabCount }, (_, i) => {
      this[`reference${i}`] = React.createRef();
      return this[`reference${i}`];
    });
  }

  /**
   * Call Dispatcher action to change the Next & Previous tab button disable
   * @param {string} actionName
   */
  private handleValidateAdditionalButtons = (actionName:string = 'additionalButtonsValidation') => {
    const { tabCount, selected, isAllValid } = this.state;

    const prevBtnValid = selected === 0;
    const nextBtnValid = selected === tabCount - 1;
    const isValid = !isAllValid;

    this.dispatch(actionName, Position.end, {
      prevBtnValid,
      nextBtnValid,
      isValid
    });
  }

  /**
   * Calls setState for selected tab and triggers callback functions
   * @param {number} selected
   */
  private setTab = (selected: number) => {
    this.setState({ selected }, () => {
      const { disableEnableAdditinalBtns } = this.props;
      if (disableEnableAdditinalBtns) {
        this.handleValidateAdditionalButtons(disableEnableAdditinalBtns);
      }
    });
  }

  /**
   * Set active tab
   * @param {number} tab
   * @param {number} num
   * @param {boolean} isIncrement
   */
  public setSelectedTab = (tab: number, num:number = 1, isIncrement: boolean = true):void => {
    const selectedTab = isIncrement ? (tab + num) : (tab - num);

    this.setTab(selectedTab);
  }

  /**
   * Get active tab
   */
  public getSelectedTab = () => this.state.selected;

  /**
   * Get tab count
   */
  public getTabCount = () => this.state.tabCount;
  /**
   * Change tab
   * @param { TabStripSelectEventArguments } event
   */
  private handleSelect = (event: TabStripSelectEventArguments) => {
    const selected = event.selected;
    this.setTab(selected);
  };

  /**
   * Set data for child
   * @param {any} data
   * @param {number} index
   * @param {any} dynamicRules
   */
  public setDataOfChild = (data:any, index:number, dynamicRules?: any): any => this.refChildren[index].current.childRef.current.handleSetData(data, dynamicRules)

  /**
   * Set data for all children
   * @param {any} data
   * @param {any} dynamicRules
   */
  public setAllData = (data:any, dynamicRules?: any) => {
    const { remoteChildren, parsingSetData } = this.props;
    const { selected, isValid } = this.state;
    if (parsingSetData) {
      data = JSON.parse(data);
    }

    if (remoteChildren) {
      let newIsValid = [...isValid];
      newIsValid[selected] = true;

      this.setDataOfChild(data[selected], selected);
      this.setState({
        isValid: newIsValid,
        data: data
      });
    } else {
      for (let i = 0; i < this.state.tabCount; i++) {
        const childDynamicRules = dynamicRules ? dynamicRules[i] : false;
        this.setDataOfChild(data[i], i, childDynamicRules);
      }
    }
  }

  /**
   * Get data from child
   * @param {number} index
   */
  public getDataOfChild = (index: number = this.state.selected) => this.refChildren[index].current.childRef.current.handleGetData()

  /**
   * Retrieves all data from the child components and returns it as either an object or an array, depending on the value of arrayGetData prop.
   * @function
   * @returns {Object | Array} - Returns all the data from child components as an object or an array based on the value of arrayGetData prop.
   */
  public getAllData = () => {
    const { stringifyGetData, arrayGetData } = this.props;
    let allData:any = arrayGetData ? [] : {};

    for (let i = 0; i < this.state.tabCount; i++) {
      const child = this.getDataOfChild(i);
      if (arrayGetData) {
        allData = [...allData, child];
      } else if (!_.isEmpty(child)) {
        allData = { ...allData, ...child };
      }
    }

    return stringifyGetData ? JSON.stringify(allData) : allData;
  }

  /**
   * Take valid of active child and set new value for isValid in state
   * @param {boolean} isChildValid
   * @param {number} index
   * @param {boolean} addNewTab
   */
  private getIsValidActiveChild = (isChildValid:boolean, index:number, addNewTab:boolean) => {
    const { isValid, isAllValid } = this.state;
    const { disableEnableActionName } = this.props;

    if ((isValid[index] !== isChildValid) || addNewTab) {
      let newIsValid = this.state.isValid;
      newIsValid[index] = isChildValid;
      let newState = {
        isValid: newIsValid
      };
      const newIsAllValid = this.isValidAllChildren(newIsValid);

      if (isAllValid !== newIsAllValid) {
        newState['isAllValid'] = newIsAllValid;
      }

      this.setState(newState, () => {
        if (disableEnableActionName && (isAllValid !== newIsAllValid)) {
          this.handleValidate(!newIsAllValid, disableEnableActionName);
        }
      });
    }
  }

  /**
   * Call Dispatcher action to change the button disable
   * @param {boolean} isValid
   * @param {string} actionName
   */
  private handleValidate = (isValid: boolean, actionName:string) => {
    this.dispatch(actionName, Position.start, {
      isValid: isValid
    });
  }

  /**
   * Return is valid all children
   * @param {boolean[]} isValid
   */
  private isValidAllChildren = (isValid:boolean[] = this.state.isValid) => isValid.every(child => child === true);

  /**
   * Props for children
   * @param {number} index
   */
  private handleChildProps = (index:number) => {
    const { remoteChildren } = this.props;
    const { data } = this.state;
    return {
      remoteChildren: remoteChildren,
      index: index,
      additionalGetData: this.additionalGetData[index],
      additionalSetData: this.additionalSetData[index],
      getIsValidActiveChild: this.getIsValidActiveChild,
      data: !!data ? data[index] : false
    };
  }

  /**
   * Create reference for children
   * @param {number} index
   */
  private handleChildRef = (index:number) => this.refChildren[index]

  /**
   * Adds item to array with possibility to put item in a desired place(index)
   * @param arr
   * @param newItem
   * @param placeIndex
   */
  private handleAddInArray(arr: any[], newItem: any, placeIndex?:number) {
    let newArray: any[] = [...arr];

    placeIndex !== undefined ? newArray.splice(placeIndex, 0, newItem) : newArray.push(newItem);

    return newArray;
  }

  /**
   * Add multiple tabs to tabPanelConfig
   * @param renderComponents
   * @param {number} focusedItem
   */
  public addMultipleTabs = (renderComponents: ADD_MULTIPLE_TABS[], focusedItem?: number) => {
    let { tabPanelConfig, isValid, isAllValid } = this.state;
    const { disableEnableActionName } = this.props;

    renderComponents.sort((itemOne, itemTwo) => {
      if (itemOne.placeIndex < itemTwo.placeIndex) {
        return -1;
      } else if (itemOne.placeIndex > itemTwo.placeIndex) {
        return 1;
      }

      return 0;
    });

    renderComponents.forEach((itemOne:ADD_MULTIPLE_TABS) => {
      const { renderComponent, title, placeIndex, disabled, moduleId } = itemOne;
      const newTab = {
        component: renderComponent,
        title,
        disabled,
        moduleId,
        key: uuid()
      };

      placeIndex !== undefined ? tabPanelConfig.splice(placeIndex, 0, newTab) : tabPanelConfig.push(newTab);
      placeIndex !== undefined ? isValid.splice(placeIndex, 0, false) : isValid.push(false);
    });

    const { length } = tabPanelConfig;
    this.setUpReferences(length);

    this.setState(prevState => ({
      tabPanelConfig: tabPanelConfig,
      isValid: isValid,
      tabCount: length,
      selected: focusedItem ? focusedItem : prevState.selected
    }), () => {
      this.tabPanelStripRef.current.setAccessibility();

      tabPanelConfig.forEach((itemOne, index) => {
        isValid[index] = this.refChildren[index].current.FORM.isAllFieldValid();
      });
      const newIsAllValid = this.isValidAllChildren(isValid);

      if (disableEnableActionName && (isAllValid !== newIsAllValid)) {
        this.handleValidate(!newIsAllValid, disableEnableActionName);
      }
    });
  }

  /**
   * Adds tab item to tabPanelConfig
   * @param {any} renderComponent
   * @param {boolean} isFocused
   * @param {string} title
   * @param {number} placeIndex
   * @param {boolean} isDisabled
   * @param {string} moduleId
   */
  addTab = (renderComponent:any, isFocused:boolean, title:string, placeIndex?:number, isDisabled?: boolean, moduleId?: string) => {
    const { tabPanelConfig, isValid } = this.state;

    const newTab = {
      component: renderComponent,
      title: title,
      disabled: isDisabled,
      moduleId,
      key: uuid()
    };

    let newTabPanelConfig = this.handleAddInArray(tabPanelConfig, newTab, placeIndex);
    let newIsValid = this.handleAddInArray(isValid, false, placeIndex);

    this.setUpReferences(newTabPanelConfig.length);

    this.setState(prevState => ({
      tabPanelConfig: newTabPanelConfig,
      isValid: newIsValid,
      tabCount: newTabPanelConfig.length,
      selected: isFocused ? placeIndex : prevState.selected
    }), () => {
      let itemIndex = placeIndex !== undefined ? placeIndex : newTabPanelConfig.length - 1;
      this.tabPanelStripRef.current.setAccessibility();
      const isChildValid = this.refChildren[itemIndex].current.FORM.isAllFieldValid();
      this.getIsValidActiveChild(isChildValid, itemIndex, true);
    });
  }

  /**
   * Delete items from array
   * @param { any[] } arr
   * @param { number | number[] } placeIndex
   */
  private handleFilterData = (arr: any[], placeIndex:number | number[]) => arr.filter((item: boolean, index: number) => {
    const isArray = _.isArray(placeIndex);

    if (isArray) {
      const placeIndexArr = placeIndex as number[];
      return !placeIndexArr.includes(index);
    } else {
      return index !== placeIndex;
    }
  })

  /**
   * Delete tab item from tabPanelConfig
   * @param {number | number[]} placeIndex
   */
  public deleteTab = (placeIndex:number | number[]) => {
    const { tabPanelConfig, isValid, isAllValid } = this.state;
    const { disableEnableActionName } = this.props;

    const filteredArray = this.handleFilterData(tabPanelConfig, placeIndex);
    const filteredIsValid = this.handleFilterData(isValid, placeIndex);
    const newIsAllValid = this.isValidAllChildren(filteredIsValid);

    if (isAllValid !== newIsAllValid && disableEnableActionName) {
      this.handleValidate(!newIsAllValid, disableEnableActionName);
    }

    const { length } = filteredArray;
    this.setUpReferences(length);

    this.setState({
      tabPanelConfig: filteredArray,
      tabCount: length,
      isValid: filteredIsValid,
      isAllValid: newIsAllValid
    });
  }

  /**
   * Set which tab will be disabled
   * @param {number | number[]} placeIndex
   * @param {boolean} disabled
   */
  public setDisabledTab = (placeIndex:number | number[], disabled:boolean):void => {
    const isArray = _.isArray(placeIndex);

    this.setState((prevState) => {
      const { tabPanelConfig: prevTabPanelConfig } = prevState;

      if (isArray) {
        const arrPlaceIndex = placeIndex as number[];
        arrPlaceIndex.forEach((item, index) => {
          prevTabPanelConfig[item].disabled = disabled;
        });
      } else {
        const index = placeIndex as number;
        prevTabPanelConfig[index].disabled = disabled;
      }

      return ({
        ...prevState,
        tabPanelConfig: prevTabPanelConfig
      });
    });
  }

  render() {
    const { tabCount, selected, tabPanelConfig, isValid } = this.state;
    const { remoteChildren, className = '', keepTabsMounted = true } = this.props;
    
    return (
      <PuzzleTabStrip
        translate={this.translate}
        handleSelect={this.handleSelect}
        tabCount={tabCount}
        selected={selected}
        className={`tp-tab-panel-form ${className}`}
        ref={this.tabPanelStripRef}
      >
        <TabStrip
          selected={selected} 
          keepTabsMounted={keepTabsMounted}
          tabContentStyle={remoteChildren ? { 
            display: 'block'
          } : { 
            display: 'none' 
          }}
        >
          {tabPanelConfig.map((tab: TAB_PANEL_CONFIG, index: number) => {
            const { component, title, key, disabled, moduleId } = tab;

            const child = {
              ...component,
              props: {
                moduleId,
                ...this.handleChildProps(index),
                ...component.props
              },
              key: key,
              ref: this.handleChildRef(index)
            };
            return (
              <TabStripTab
                key={key}
                data-cy={'tp-tab-panel-form-tab'}
                disabled={disabled}
                title={
                  <HBox htmlTag={'span'} gap={1}>
                    {title}
                    <HBox
                      alignItemsStart
                      className={'tp-tab-panel-icon-wrapper'}
                      dataCy={'tp-tab-panel-icon-wrapper'}
                      htmlTag={'span'}
                    >{(!isValid[index] && (selected !== index)) &&
                    <span
                      data-cy={'tp-tab-panel-icon'}
                      className={'tp-icon tp-circle tp-color-error'}
                    />}
                    </HBox>
                  </HBox>
                }
              >
                {remoteChildren && child}
              </TabStripTab>
            );
          }
          )}
        </TabStrip>

        {!remoteChildren &&
          <div
            className={'k-py-4 k-px-2'}
            data-cy={'tp-tab-panel-form-tab'}
          >
            {tabPanelConfig.map((item: TAB_PANEL_CONFIG, itemIndex: number) => {
              const { component, key, moduleId } = item;
              const isActive = selected === itemIndex;
              const child = {
                ...component,
                props: {
                  moduleId,
                  ...this.handleChildProps(itemIndex),
                  ...component.props
                },
                ref: this.handleChildRef(itemIndex)
              };
              return (
                <div
                  key={key}
                  style={isActive ? {
                    display: 'block'
                  } : {
                    display: 'none'
                  }}
                >
                  {child}
                </div>
              );
            })}
          </div>
        }
      </PuzzleTabStrip>
    );
  }
}

